import {
  type Chain,
  type DynamicClient,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  formatWalletProviderGroupKey,
  formatWalletProviderKey,
  getActiveNetworkIdFromLastKnownRegistry,
  getBuffer,
  switchActiveNetworkInLastKnownRegistry,
} from '@dynamic-labs-sdk/client/core';
import {
  DYNAMIC_WAAS_METADATA,
  createWaasProvider,
  getAllUserWaasAddressesForChain,
} from '@dynamic-labs-sdk/client/waas/core';
import { WalletProviderEnum } from '@dynamic-labs/sdk-api-core';
import { SuiClient } from '@mysten/sui/client';

import { suiExecuteCheckoutTransaction } from '../../../utils/suiExecuteCheckoutTransaction';
import { suiTransferAmount } from '../../../utils/suiTransferAmount/suiTransferAmount';
import type { WaasSuiWalletProvider } from '../../WaasSuiWalletProvider.types';

export const createWalletProviderForWaasSui = (
  sdkClient: DynamicClient
): WaasSuiWalletProvider => {
  const chain: Chain = 'SUI';

  const walletProviderType = WalletProviderEnum.EmbeddedWallet;

  const key = formatWalletProviderKey({
    chain,
    displayName: DYNAMIC_WAAS_METADATA.displayName,
    walletProviderType,
  });

  const waasProvider = createWaasProvider({ chain, sdkClient });

  const getActiveNetworkId: WaasSuiWalletProvider['getActiveNetworkId'] =
    async () =>
      getActiveNetworkIdFromLastKnownRegistry({
        client: sdkClient,
        walletProviderKey: key,
      });

  const getSuiClient: WaasSuiWalletProvider['getSuiClient'] = async ({
    walletAccount,
  }) => {
    const activeNetworkData = await getActiveNetworkData(
      { walletAccount },
      sdkClient
    );
    assertDefined(activeNetworkData, 'No active network data found');

    const url = activeNetworkData.networkData?.rpcUrls.http[0];
    assertDefined(url, 'No RPC URL found for active network');

    return new SuiClient({ url });
  };

  const switchActiveNetwork: WaasSuiWalletProvider['switchActiveNetwork'] =
    async ({ networkId }) =>
      switchActiveNetworkInLastKnownRegistry({
        client: sdkClient,
        networkId,
        walletProviderKey: key,
      });

  const getConnectedAddresses: WaasSuiWalletProvider['getConnectedAddresses'] =
    async () => {
      const waasSolanaAddresses = getAllUserWaasAddressesForChain(
        { chain },
        sdkClient
      );

      return {
        addresses: waasSolanaAddresses,
      };
    };

  const signAndExecuteTransaction: WaasSuiWalletProvider['signAndExecuteTransaction'] =
    async ({ transaction, walletAccount }) => {
      const { bytes, signature } = await signTransaction({
        transaction,
        walletAccount,
      });

      const suiClient = await getSuiClient({ walletAccount });

      const response = await suiClient.executeTransactionBlock({
        signature,
        transactionBlock: bytes,
      });

      return {
        ...response,
        bytes,
        effects: response.effects?.toString() ?? '',
        signature,
      };
    };

  const signTransaction: WaasSuiWalletProvider['signTransaction'] = async ({
    transaction,
    walletAccount,
  }) => {
    const suiClient = await getSuiClient({ walletAccount });
    const txBytes = await transaction.build({ client: suiClient });
    const txBytesBase64 = getBuffer().from(txBytes).toString('base64');
    const txString = getBuffer().from(txBytes).toString('hex');

    const { signature } = await waasProvider.signSerializedTransaction({
      serializedTransaction: txString,
      walletAccount,
    });

    return { bytes: txBytesBase64, signature };
  };

  const signMessage: WaasSuiWalletProvider['signMessage'] = async ({
    message,
    walletAccount,
  }) => {
    assertDefined(walletAccount, 'Wallet account is required');

    return waasProvider.signMessage({ message, walletAccount });
  };

  return {
    ...waasProvider,
    chain,
    executeCheckoutTransaction: (args) =>
      suiExecuteCheckoutTransaction(args, sdkClient),
    getActiveNetworkId,
    getConnectedAddresses,
    getSuiClient,
    groupKey: formatWalletProviderGroupKey(DYNAMIC_WAAS_METADATA.displayName),
    key,
    metadata: {
      displayName: DYNAMIC_WAAS_METADATA.displayName,
      icon: DYNAMIC_WAAS_METADATA.icon,
    },
    signAndExecuteTransaction,
    signMessage,
    signTransaction,
    switchActiveNetwork,
    transferAmount: (args) => suiTransferAmount(args, sdkClient),
    walletProviderType,
  };
};
